import { AxiosRequestConfig, CancelToken } from 'axios';
import BaseApi from '@/_types/api/base.api.class';
import {TEvent, TEventSettings} from '@/_types/event.type';
import { TContact } from '@/_types/contact.type';
import { TListOfBroadcastsItem } from '@/_types/list-of-broadcasts.type';
import { TApiListResponse } from '@/_types/api/api-list-response.type';
import { TEventRequest } from '@/_modules/events/types/event.type';
import { TContactTag } from '@/_types/contact-tag.type';
import EventHelper from '@/_helpers/event.helper';
import ContactHelper from '@/_helpers/contact.helper';

export type TGetEventParams = {
  eventId: number;
}

export type TGetEventSettingsParams = {
  eventId: number;
}

export type TPatchEventSettingsParams = {
  eventId: number;
  layout: any;
}

export type TGetEventContactParams = {
  eventId: number;
}

export type TEditEventParams = {
  eventId: number;
  formData: TEventRequest;
}

export type TGetListOfContactTagsParams = {
  eventId: number;
}

export type TCreateContactTagRequestParams = {
  eventId: number;
  name: string;
  parent_id?: number;
}

export type TDeleteContactTagParams = {
  eventId: number;
  tagId: number;
}

export class EventApi extends BaseApi {

  public async getEvent(params: TGetEventParams, cancelToken?: CancelToken): Promise<TEvent> {
    const { eventId } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}`,
      method: 'GET',
      cancelToken,
    };

    let response;

    try {
      response = await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return EventHelper.responseToEventConverter(response.data);
  }

  public async getEventSettings(params: TGetEventSettingsParams, cancelToken?: CancelToken): Promise<TEventSettings> {
    const { eventId } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/setting`,
      method: 'GET',
      cancelToken,
    };

    let response;

    try {
      response = await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return response.data;
  }

  public async patchEventSettings(params: TPatchEventSettingsParams, cancelToken?: CancelToken): Promise<void> {
    const { eventId, layout } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/setting`,
      method: 'PATCH',
      params: {
        layout: JSON.stringify(layout)
      },
      cancelToken,
    };

    try {
      await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

  }

  public async createEvent(params: TEventRequest, cancelToken?: CancelToken): Promise<TEvent> {

    const config: AxiosRequestConfig = {
      url: 'event',
      method: 'POST',
      cancelToken,
      params: params
    };

    let response;
    try {
      response = await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return EventHelper.responseToEventConverter(response.data);
  }

  public async editEvent(params: TEditEventParams, cancelToken?: CancelToken): Promise<TEvent> {
    const {eventId, formData} = params;

    const config: AxiosRequestConfig = {
      url: `event/${eventId}`,
      method: 'PATCH',
      cancelToken,
      params: formData
    };

    let response;
    try {
      response = await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return EventHelper.responseToEventConverter(response.data);
  }

  public async getContact(params: TGetEventContactParams, cancelToken?: CancelToken): Promise<TContact> {
    const { eventId } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/contact/info`,
      method: 'GET',
      cancelToken,
    };

    let response;
    try {
      response = await this.axios.request<any>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return ContactHelper.responseToContactConverter(response.data);
  }

  public async getListOfBroadcasts(params: TGetEventContactParams, cancelToken?: CancelToken): Promise<TApiListResponse<TListOfBroadcastsItem>> {
    const { eventId } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/stream/list`,
      method: 'GET',
      cancelToken,
    };

    let response;
    try {
      response = await this.axios.request<TApiListResponse<any>>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    response.data.List = response.data.List.map(item => {
      return EventHelper.responseToBroadcastItemConverter(item);
    });

    return response.data;
  }

  public async getListOfContactTags(params: TGetListOfContactTagsParams, cancelToken?: CancelToken): Promise<TContactTag[]> {
    const { eventId } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/contact_tag/list`,
      method: 'GET',
      cancelToken,
    };

    let response;
    try {
      response = await this.axios.request<TApiListResponse<any>>(config);
    } catch (error) {
      throw this.responseError(error);
    }

    return response.data.List.map(item => {
      return EventHelper.responseToContactTagConverter(item);
    });
  }

  public async createContactTag(params: TCreateContactTagRequestParams, cancelToken?: CancelToken): Promise<void> {
    const { eventId, name } = params;
    const config: AxiosRequestConfig = {
      url: `event/${eventId}/contact_tag`,
      method: 'POST',
      cancelToken,
      params: { name }
    };

    try {
      await this.axios.request<void>(config);
    } catch (error) {
      throw this.responseError(error);
    }

  }

  public async deleteContactTag(params: TDeleteContactTagParams, cancelToken?: CancelToken): Promise<void> {
    const { eventId, tagId } = params;

    const config: AxiosRequestConfig = {
      url: `/event/${eventId}/contact_tag/${tagId}`,
      method: 'DELETE',
      cancelToken,
    };

    let response;
    try {
      response = await this.axios.request<void>(config);
    } catch (error) {
      throw this.responseError(error);
    }
    return response.data;
  }

}

const eventApi = new EventApi();
export default eventApi;
